import React, { useEffect, useMemo, useRef, useState } from "react";

// Formulaire de prise de rendez-vous avec un conseiller, branché sur l'API booking
// (/v1/public/booking/:slug/slots et /book). Le conseiller est attribué côté serveur.
const API = process.env.REACT_APP_API_ENDPOINT;
const MAX_DAYS = 10;

const dayKeyOf = (ms) => {
  const d = new Date(ms);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const dayDate = (key) => {
  const [y, m, d] = key.split("-").map(Number);
  return new Date(y, m - 1, d);
};
const fmtTime = (ms) =>
  new Date(ms).toLocaleTimeString("fr-FR", {
    hour: "2-digit",
    minute: "2-digit",
  });
const fmtLongDay = (key) =>
  dayDate(key).toLocaleDateString("fr-FR", {
    weekday: "long",
    day: "numeric",
    month: "long",
  });

// Questions de qualification (facultatives) : aident le conseiller à préparer l'appel.
// Les réponses sont ajoutées au message de la réservation (visibles dans l'e-mail et l'admin).
const QUESTIONS = [
  {
    key: "needs",
    label: "Qu’est-ce qui vous intéresse ?",
    multi: true,
    options: [
      "Créer ma boutique en ligne",
      "Faire livrer mes commandes",
      "Encaisser à la livraison",
      "Connecter ma boutique Shopify / WooCommerce",
    ],
  },
  {
    key: "activity",
    label: "Votre activité",
    options: [
      "Mode et accessoires",
      "Cuisine et restauration",
      "Beauté et cosmétiques",
      "Épicerie",
      "Électronique",
      "Autre",
    ],
  },
  {
    key: "channels",
    label: "Où vendez-vous aujourd’hui ?",
    multi: true,
    options: [
      "WhatsApp",
      "Facebook",
      "Instagram",
      "TikTok",
      "Boutique physique",
      "Site web",
    ],
  },
  {
    key: "volume",
    label: "Combien de livraisons par semaine ?",
    options: ["Aucune pour l’instant", "1 à 10", "11 à 50", "Plus de 50"],
  },
  {
    key: "delivery",
    label: "Comment livrez-vous aujourd’hui ?",
    options: [
      "Je livre moi-même",
      "Mes propres livreurs",
      "Une société de livraison",
      "Je ne livre pas encore",
    ],
  },
  {
    key: "content",
    label: "Faites-vous des vidéos ou des publicités pour vendre ?",
    options: [
      "Oui, des vidéos (TikTok, Reels)",
      "Oui, des publicités sponsorisées",
      "Les deux",
      "Pas encore",
    ],
  },
];

function buildMessage(answers, note) {
  const lines = QUESTIONS.map((q) => {
    const v = answers[q.key];
    const text = Array.isArray(v) ? v.join(", ") : v;
    return text ? `${q.label.replace(/\s*\?$/, "")} : ${text}` : null;
  }).filter(Boolean);
  const free = (note || "").trim();
  if (free) lines.push(`Besoin : ${free}`);
  return lines.join("\n");
}

// Origine de la visite (pub Meta, etc.) ajoutée à la source du rendez-vous, pour savoir
// dans la gestion des RDV quels rendez-vous viennent d'une campagne. Champ source : 255 car. max.
const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content"];
export function sourceWithUtm(base) {
  let parts = [base];
  try {
    const sp = new URLSearchParams(window.location.search);
    let utm = UTM_KEYS.map((k) => sp.get(k) || "");
    if (utm.some(Boolean)) {
      try { sessionStorage.setItem("ogg_booking_utm", JSON.stringify(utm)); } catch (_) {}
    } else {
      try { utm = JSON.parse(sessionStorage.getItem("ogg_booking_utm") || "null") || utm; } catch (_) {}
    }
    UTM_KEYS.forEach((k, i) => { if (utm[i]) parts.push(`${k.replace("utm_", "")}=${utm[i]}`); });
    if (!utm.some(Boolean) && sp.get("fbclid")) parts.push("source=facebook");
  } catch (_) {}
  return parts.join(" | ").slice(0, 250);
}

const inputCls =
  "w-full h-12 rounded-xl border border-gray-300 bg-white px-4 text-base text-gray-900 outline-none transition focus:border-primary-custom focus:ring-2 focus:ring-primary-custom/30";
const labelCls = "block text-sm font-semibold text-gray-800";
const STEPS = ["Créneau", "Votre activité", "Coordonnées"];

export default function BookingForm({
  slug = "demo",
  source = "vitrine",
  whatsappLink = "",
  onBooked,
}) {
  const tz = useMemo(() => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
      return "";
    }
  }, []);
  const [slots, setSlots] = useState([]);
  const [duration, setDuration] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [day, setDay] = useState(null);
  const [slot, setSlot] = useState(null);
  const [form, setForm] = useState({
    name: "",
    businessName: "",
    phone: "",
    email: "",
    city: "",
    message: "",
  });
  const [answers, setAnswers] = useState({});
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [step, setStep] = useState(1);
  const topRef = useRef(null);

  const goTo = (n) => {
    setError("");
    setStep(n);
    if (topRef.current)
      topRef.current.scrollIntoView({ block: "start", behavior: "smooth" });
  };
  const next = () => {
    if (step === 1 && !slot)
      return setError("Choisissez un créneau pour continuer.");
    goTo(step + 1);
  };

  const load = async () => {
    setLoading(true);
    setLoadError("");
    try {
      const res = await fetch(
        `${API}/v1/public/booking/${encodeURIComponent(slug)}/slots`,
      );
      if (!res.ok) throw new Error();
      const data = await res.json();
      setDuration(data.durationMin || null);
      setSlots(
        (Array.isArray(data.slots) ? data.slots : []).filter(
          (s) => s.free !== false,
        ),
      );
    } catch (_) {
      setLoadError("Impossible de charger les créneaux pour le moment.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const byDay = useMemo(() => {
    const map = {};
    slots.forEach((s) => {
      const k = dayKeyOf(s.startUtc);
      (map[k] = map[k] || []).push(s);
    });
    return map;
  }, [slots]);
  const days = useMemo(
    () => Object.keys(byDay).sort().slice(0, MAX_DAYS),
    [byDay],
  );

  useEffect(() => {
    if (days.length && (!day || !byDay[day])) setDay(days[0]);
  }, [days, day, byDay]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const pick = (q, option) =>
    setAnswers((a) => {
      if (!q.multi)
        return { ...a, [q.key]: a[q.key] === option ? undefined : option };
      const cur = a[q.key] || [];
      return {
        ...a,
        [q.key]: cur.includes(option)
          ? cur.filter((x) => x !== option)
          : [...cur, option],
      };
    });

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setError("");
    if (!slot) {
      setStep(1);
      return setError("Choisissez un créneau.");
    }
    if (!form.name.trim()) return setError("Votre nom est requis.");
    if (form.phone.replace(/\D/g, "").length < 8)
      return setError("Votre numéro WhatsApp est requis.");
    if (!form.businessName.trim())
      return setError("Le nom de votre boutique est requis.");
    setBusy(true);
    try {
      const res = await fetch(
        `${API}/v1/public/booking/${encodeURIComponent(slug)}/book`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            ...form,
            message: buildMessage(answers, form.message),
            slotStartUtc: slot,
            clientTimezone: tz,
            source: sourceWithUtm(source),
          }),
        },
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const msg = data?.message || "";
        if (msg.startsWith("SLOT_TAKEN")) {
          setStep(1);
          setError("Ce créneau vient d’être pris. Choisissez-en un autre.");
          setSlot(null);
          await load();
          return;
        }
        if (msg.startsWith("SLOT_INVALID")) {
          setStep(1);
          setError("Ce créneau n’est plus valide. Choisissez-en un autre.");
          setSlot(null);
          await load();
          return;
        }
        throw new Error();
      }
      setDone({ day: dayKeyOf(slot), time: fmtTime(slot) });
      if (onBooked) onBooked();
    } catch (_) {
      setError("Réservation impossible pour le moment. Réessayez.");
    } finally {
      setBusy(false);
    }
  };

  const whatsappFallback = whatsappLink ? (
    <a
      href={whatsappLink}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex h-11 items-center justify-center rounded-xl border border-gray-300 bg-white px-4 text-sm font-semibold text-gray-800 hover:bg-gray-100"
    >
      Nous écrire sur WhatsApp
    </a>
  ) : null;

  if (done) {
    return (
      <div className="flex min-h-[320px] flex-col items-center justify-center gap-3 py-6 text-center">
        <div
          className="flex h-16 w-16 items-center justify-center rounded-full bg-green-100 text-3xl text-green-700"
          aria-hidden="true"
        >
          ✓
        </div>
        <p className="text-2xl font-bold text-gray-900">
          Rendez-vous confirmé !
        </p>
        <p className="max-w-sm text-gray-600">
          Un conseiller OuiGoo vous contacte le {fmtLongDay(done.day)} à{" "}
          {done.time}.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} noValidate className="space-y-5">
      <div ref={topRef} className="scroll-mt-4">
        <div className="flex items-center justify-between text-xs font-semibold text-gray-500">
          <span>
            Étape {step} sur {STEPS.length} :{" "}
            <span className="text-gray-900">{STEPS[step - 1]}</span>
          </span>
          {slot && step > 1 ? (
            <span className="text-gray-700">
              {fmtLongDay(dayKeyOf(slot))} · {fmtTime(slot)}
            </span>
          ) : null}
        </div>
        <div className="mt-2 grid grid-cols-3 gap-1.5" aria-hidden="true">
          {STEPS.map((label, i) => (
            <div
              key={label}
              className={`h-1.5 rounded-full ${i < step ? "bg-primary-custom" : "bg-gray-200"}`}
            />
          ))}
        </div>
      </div>

      {step === 1 ? (
        <div>
          <p className="mb-3 text-sm font-bold text-gray-900">
            Choisissez un créneau
          </p>

          {loading ? (
            <div aria-busy="true" aria-label="Chargement des créneaux">
              <div className="flex gap-2 overflow-hidden">
                {[0, 1, 2, 3].map((i) => (
                  <div
                    key={i}
                    className="h-14 w-20 flex-none animate-pulse rounded-xl bg-gray-200"
                  />
                ))}
              </div>
              <div className="mt-3 grid grid-cols-3 gap-2 sm:grid-cols-4">
                {[0, 1, 2, 3, 4, 5].map((i) => (
                  <div
                    key={i}
                    className="h-11 animate-pulse rounded-xl bg-gray-200"
                  />
                ))}
              </div>
              <p className="mt-3 text-sm text-gray-500">
                Chargement des créneaux…
              </p>
            </div>
          ) : loadError || !days.length ? (
            <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
              <p className="font-semibold text-gray-900">
                {loadError || "Aucun créneau disponible pour le moment."}
              </p>
              <p className="mt-1 text-sm text-gray-600">
                Réessayez un peu plus tard ou écrivez-nous directement : un
                conseiller vous répondra.
              </p>
              <div className="mt-3 flex flex-col gap-2 sm:flex-row">
                <button
                  type="button"
                  onClick={load}
                  className="inline-flex h-11 items-center justify-center rounded-xl bg-gray-900 px-4 text-sm font-semibold text-white hover:bg-gray-800"
                >
                  Réessayer
                </button>
                {whatsappFallback}
              </div>
            </div>
          ) : (
            <>
              <div
                className="-mx-1 flex snap-x gap-2 overflow-x-auto px-1 pb-2"
                role="radiogroup"
                aria-label="Jour"
              >
                {days.map((k) => {
                  const d = dayDate(k);
                  const on = k === day;
                  return (
                    <button
                      key={k}
                      type="button"
                      role="radio"
                      aria-checked={on}
                      onClick={() => {
                        setDay(k);
                        setSlot(null);
                      }}
                      className={`flex h-14 w-[72px] flex-none snap-start sm:h-16 sm:w-[88px] flex-col items-center justify-center rounded-xl border text-sm transition ${
                        on
                          ? "border-gray-900 bg-gray-900 text-white"
                          : "border-gray-300 bg-white text-gray-800 hover:border-gray-500"
                      }`}
                    >
                      <span className="text-xs capitalize opacity-80">
                        {d
                          .toLocaleDateString("fr-FR", { weekday: "short" })
                          .replace(".", "")}
                      </span>
                      <span className="font-bold">
                        {d
                          .toLocaleDateString("fr-FR", {
                            day: "numeric",
                            month: "short",
                          })
                          .replace(".", "")}
                      </span>
                    </button>
                  );
                })}
              </div>
              <div
                className="mt-3 grid grid-cols-3 gap-2 sm:grid-cols-4 sm:gap-3 lg:grid-cols-5"
                role="radiogroup"
                aria-label="Heure"
              >
                {(byDay[day] || []).map((s) => {
                  const on = s.startUtc === slot;
                  return (
                    <button
                      key={s.startUtc}
                      type="button"
                      role="radio"
                      aria-checked={on}
                      onClick={() => setSlot(s.startUtc)}
                      className={`h-11 rounded-xl border text-sm font-semibold transition sm:h-12 sm:text-base ${
                        on
                          ? "border-primary-custom bg-primary-custom text-white"
                          : "border-gray-300 bg-white text-gray-800 hover:border-gray-500"
                      }`}
                    >
                      {fmtTime(s.startUtc)}
                    </button>
                  );
                })}
              </div>
              <p className="mt-2 text-xs text-gray-500">
                {slot
                  ? `Rendez-vous le ${fmtLongDay(day)} à ${fmtTime(slot)}${duration ? ` · ${duration} min` : ""}`
                  : "Heures affichées dans votre fuseau horaire."}
              </p>
            </>
          )}
        </div>
      ) : null}

      {step === 2 ? (
        <div className="space-y-6">
          <div>
            <p className="text-sm font-bold text-gray-900">
              Parlez-nous de votre activité
            </p>
            <p className="text-xs text-gray-500">
              Facultatif, pour que le conseiller prépare votre appel.
            </p>
          </div>
          {QUESTIONS.map((q) => (
            <fieldset key={q.key}>
              <legend className="mb-2 text-sm font-semibold text-gray-800">
                {q.label}
                {q.multi ? (
                  <span className="font-normal text-gray-500">
                    {" "}
                    (plusieurs choix possibles)
                  </span>
                ) : null}
              </legend>
              <div className="flex flex-wrap gap-2 sm:gap-3">
                {q.options.map((option) => {
                  const v = answers[q.key];
                  const on = q.multi
                    ? (v || []).includes(option)
                    : v === option;
                  return (
                    <button
                      key={option}
                      type="button"
                      aria-pressed={on}
                      onClick={() => pick(q, option)}
                      className={`min-h-[40px] rounded-full border px-3.5 py-2 text-sm transition sm:min-h-[46px] sm:px-5 sm:text-[15px] ${
                        on
                          ? "border-gray-900 bg-gray-900 font-semibold text-white"
                          : "border-gray-300 bg-white text-gray-800 hover:border-gray-500"
                      }`}
                    >
                      {option}
                    </button>
                  );
                })}
              </div>
            </fieldset>
          ))}
        </div>
      ) : null}

      {step === 3 ? (
        <div className="space-y-5">
          <p className="text-sm font-bold text-gray-900">Vos coordonnées</p>
          <div className="grid gap-4 sm:grid-cols-2">
            <label className={labelCls}>
              Nom et prénom <span className="text-red-600">*</span>
              <input
                className={`${inputCls} mt-1`}
                value={form.name}
                onChange={set("name")}
                autoComplete="name"
                placeholder="Ex. Awa Koffi"
              />
            </label>
            <label className={labelCls}>
              Numéro WhatsApp <span className="text-red-600">*</span>
              <input
                className={`${inputCls} mt-1`}
                type="tel"
                inputMode="tel"
                value={form.phone}
                onChange={set("phone")}
                autoComplete="tel"
                placeholder="+229 01 00 00 00 00"
              />
            </label>
            <label className={labelCls}>
              Nom de votre boutique <span className="text-red-600">*</span>
              <input
                className={`${inputCls} mt-1`}
                value={form.businessName}
                onChange={set("businessName")}
                autoComplete="organization"
                placeholder="Ex. Sophie Shop"
              />
            </label>
            <label className={labelCls}>
              Ville
              <input
                className={`${inputCls} mt-1`}
                value={form.city}
                onChange={set("city")}
                autoComplete="address-level2"
                placeholder="Ex. Cotonou"
                list="booking-cities"
              />
              <datalist id="booking-cities">
                {["Cotonou", "Abomey-Calavi", "Porto-Novo", "Ouidah", "Bohicon", "Parakou", "Lomé"].map((c) => (
                  <option key={c} value={c} />
                ))}
              </datalist>
            </label>
          </div>
          <label className={labelCls}>
            E-mail{" "}
            <span className="font-normal text-gray-500">(facultatif)</span>
            <input
              className={`${inputCls} mt-1`}
              type="email"
              inputMode="email"
              value={form.email}
              onChange={set("email")}
              autoComplete="email"
              placeholder="vous@exemple.com"
            />
          </label>
          <label className={labelCls}>
            Votre besoin{" "}
            <span className="font-normal text-gray-500">(facultatif)</span>
            <textarea
              className={`${inputCls} mt-1 h-auto py-3`}
              rows={3}
              value={form.message}
              onChange={set("message")}
              placeholder="Ex. Je vends sur Instagram et je veux livrer à Cotonou"
            />
          </label>
        </div>
      ) : null}

      {error ? (
        <div
          role="alert"
          className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm font-semibold text-red-700"
        >
          {error}
        </div>
      ) : null}

      <div className="flex gap-3">
        {step > 1 ? (
          <button
            type="button"
            onClick={() => goTo(step - 1)}
            className="inline-flex h-14 items-center justify-center rounded-xl border border-gray-300 bg-white px-5 text-base font-semibold text-gray-800 hover:bg-gray-100"
          >
            Retour
          </button>
        ) : null}
        {step < STEPS.length ? (
          <button
            type="button"
            onClick={next}
            disabled={step === 1 && (loading || !days.length)}
            className="inline-flex h-14 flex-1 items-center justify-center rounded-xl bg-primary-custom px-5 text-base font-bold text-white transition hover:opacity-90 disabled:opacity-60"
          >
            Continuer
          </button>
        ) : (
          <button
            type="submit"
            disabled={busy || loading || !days.length}
            className="inline-flex h-14 flex-1 items-center justify-center rounded-xl bg-primary-custom px-5 text-base font-bold text-white transition hover:opacity-90 disabled:opacity-60"
          >
            {busy ? "Réservation en cours" : "Confirmer mon rendez-vous"}
          </button>
        )}
      </div>
      {step === 2 ? (
        <button
          type="button"
          onClick={() => goTo(3)}
          className="w-full text-center text-sm font-semibold text-gray-500 hover:underline"
        >
          Passer cette étape
        </button>
      ) : null}
      {step === 3 ? (
        <p className="text-xs text-gray-500">
          En envoyant ce formulaire, vous acceptez d’être recontacté par OuiGoo.
        </p>
      ) : null}
    </form>
  );
}
